import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { UiRecaptchaChallengeFitService } from './recaptcha-challenge-fit.service';
import { UiRecaptchaLoaderService } from './recaptcha-loader.service';
import { UiGrecaptcha } from './recaptcha.types';

export type UiRecaptchaTheme = 'light' | 'dark';
export type UiRecaptchaSize = 'normal' | 'compact' | 'invisible';

/**
 * Átomo: widget de Google reCAPTCHA v2 (casilla visible por defecto).
 *
 * Carga el script de Google de forma lazy y emite el token al resolverse.
 * Para resetear el widget después de un login fallido, llamar a `reset()`.
 */
@Component({
  selector: 'ui-recaptcha',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './recaptcha.component.html',
  styleUrl: './recaptcha.component.scss',
  host: {
    class: 'ui-recaptcha-host',
    '[class.ui-recaptcha-host--fluid]': 'fluid() && size() === "normal"',
  },
})
export class UiRecaptchaComponent implements OnDestroy {
  private readonly loader = inject(UiRecaptchaLoaderService);
  private readonly challengeFit = inject(UiRecaptchaChallengeFitService);

  readonly siteKey = input.required<string>();
  readonly theme = input<UiRecaptchaTheme>('light');
  readonly size = input<UiRecaptchaSize>('normal');
  readonly language = input('es');
  /** Ajusta la casilla normal al ancho del contenedor. */
  readonly fluid = input(false);
  /** Ajusta el desafío visual de Google al viewport. */
  readonly fitChallengeToViewport = input(false);

  readonly resolved = output<string>();
  readonly expired = output<void>();
  readonly errored = output<void>();

  protected readonly hostRef =
    viewChild.required<ElementRef<HTMLDivElement>>('host');
  private readonly wrapRef =
    viewChild.required<ElementRef<HTMLDivElement>>('wrap');

  protected readonly isReady = signal(false);
  protected readonly hasError = signal(false);

  private widgetId: number | null = null;
  private grecaptcha: UiGrecaptcha | null = null;
  private scaleObserver: ResizeObserver | null = null;
  private challengeFitConnected = false;

  constructor() {
    afterNextRender(() => {
      this.observeScale();
      void this.render();
    });
  }

  ngOnDestroy(): void {
    this.scaleObserver?.disconnect();
    this.scaleObserver = null;
    if (this.challengeFitConnected) {
      this.challengeFit.disconnect();
      this.challengeFitConnected = false;
    }
    this.tryReset();
    this.widgetId = null;
    this.grecaptcha = null;
  }

  /** Resetea el widget para forzar al usuario a marcarlo de nuevo. */
  reset(): void {
    this.tryReset();
  }

  /** Devuelve el token actual o cadena vacía si no hay. */
  getToken(): string {
    if (!this.grecaptcha || this.widgetId === null) return '';
    try {
      return this.grecaptcha.getResponse(this.widgetId) ?? '';
    } catch {
      return '';
    }
  }

  private async render(): Promise<void> {
    const key = this.siteKey().trim();
    if (!key) {
      this.hasError.set(true);
      this.errored.emit();
      return;
    }

    try {
      const g = await this.loader.load(this.language());
      this.grecaptcha = g;

      this.widgetId = g.render(this.hostRef().nativeElement, {
        sitekey: key,
        theme: this.theme(),
        size: this.size(),
        hl: this.language(),
        callback: (token: string) => this.resolved.emit(token),
        'expired-callback': () => this.expired.emit(),
        'error-callback': () => {
          this.hasError.set(true);
          this.errored.emit();
        },
      });
      this.isReady.set(true);
      if (this.fitChallengeToViewport()) {
        this.challengeFit.connect();
        this.challengeFitConnected = true;
      }
    } catch {
      this.hasError.set(true);
      this.errored.emit();
    }
  }

  /**
   * Mide el ancho real del contenedor. El modo fluido solo aplica a la casilla
   * normal; `compact` e `invisible` conservan su contrato nativo de Google.
   */
  private observeScale(): void {
    const wrap = this.wrapRef().nativeElement;
    const apply = (width: number): void => {
      const dimensions = this.nativeDimensions();
      const scale =
        this.fluid() && this.size() === 'normal' && width > 0
          ? width / dimensions.width
          : 1;

      wrap.style.setProperty('--ui-recaptcha-scale', String(scale));
      wrap.style.setProperty(
        '--ui-recaptcha-native-width',
        `${dimensions.width}px`,
      );
      wrap.style.setProperty(
        '--ui-recaptcha-native-height',
        `${dimensions.height}px`,
      );
      wrap.style.setProperty(
        '--ui-recaptcha-rendered-height',
        `${dimensions.height * scale}px`,
      );
    };

    apply(wrap.clientWidth);

    if (typeof ResizeObserver === 'undefined') return;
    this.scaleObserver = new ResizeObserver((entries) => {
      apply(entries[0]?.contentRect.width ?? wrap.clientWidth);
    });
    this.scaleObserver.observe(wrap);
  }

  private nativeDimensions(): { width: number; height: number } {
    if (this.size() === 'compact') return { width: 164, height: 144 };
    if (this.size() === 'invisible') return { width: 0, height: 0 };
    return { width: 304, height: 78 };
  }

  private tryReset(): void {
    if (!this.grecaptcha || this.widgetId === null) return;
    try {
      this.grecaptcha.reset(this.widgetId);
    } catch {
      // El widget puede haber sido destruido por Google; ignorar.
    }
  }
}
